import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInfoCircle,
    faStore,
    faMagic,
    faSpinner,
    faSearch,
    faPlus,
    faTrash,
    faPlay
} from '@fortawesome/free-solid-svg-icons';
import { Chain } from '../../coffee_clusters/hooks/useChains';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui';
import { Button, Input } from '../../../ui';
import { useChainShops } from '../hooks/useChainShops';
import { useChainRules } from '../hooks/useChainRules';
import { useChainMutations } from '../hooks/useChainMutations';
import { toast } from 'react-toastify';

interface ChainDetailPanelProps {
    chain: Chain | null;
    onRefetchChains: () => void;
}

export function ChainDetailPanel({ chain, onRefetchChains }: ChainDetailPanelProps) {
    const [activeTab, setActiveTab] = useState('details');
    const [shopSearchQuery, setShopSearchQuery] = useState('');
    const [shopSearchResults, setShopSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedShopIds, setSelectedShopIds] = useState<Set<string>>(new Set());
    const [newRuleValue, setNewRuleValue] = useState('');
    const [newRuleOperator, setNewRuleOperator] = useState<'contains' | 'startsWith' | 'equals'>('contains');
    const [isAddingRule, setIsAddingRule] = useState(false);
    const [isRunningRules, setIsRunningRules] = useState(false);

    const { shops, total, loading: shopsLoading, refetch: refetchShops } = useChainShops(chain?._id ?? null);
    const { rules, loading: rulesLoading, refetch: refetchRules } = useChainRules(chain?._id ?? null);
    const mutations = useChainMutations(onRefetchChains);

    if (!chain) {
        return (
            <div className="rounded-lg border border-ink/15 p-6 text-center text-muted-foreground text-sm">
                Select a chain to view details
            </div>
        );
    }

    const handleSearchShops = async () => {
        if (!chain._id || !shopSearchQuery.trim()) return;
        setIsSearching(true);
        try {
            const results = await mutations.searchShops(chain._id, shopSearchQuery.trim());
            setShopSearchResults(results);
        } catch {
            toast.error('Search failed');
        } finally {
            setIsSearching(false);
        }
    };

    const handleAssignShops = async () => {
        if (!chain._id || selectedShopIds.size === 0) return;
        try {
            const res = await mutations.assign(chain._id, [...selectedShopIds]);
            toast.success(`Assigned ${res?.assigned ?? 0} shops`);
            setSelectedShopIds(new Set());
            setShopSearchResults([]);
            refetchShops();
        } catch {
            toast.error('Assign failed');
        }
    };

    const handleUnassignShops = async (shopIds: string[]) => {
        if (!chain._id) return;
        try {
            const res = await mutations.unassign(chain._id, shopIds);
            toast.success(`Unassigned ${res?.unassigned ?? 0} shops`);
            refetchShops();
        } catch {
            toast.error('Unassign failed');
        }
    };

    const handleAddRule = async () => {
        if (!chain._id || !newRuleValue.trim()) return;
        setIsAddingRule(true);
        try {
            await mutations.createRule(chain._id, {
                field: 'name',
                operator: newRuleOperator,
                value: newRuleValue.trim()
            });
            toast.success('Rule added');
            setNewRuleValue('');
            refetchRules();
        } catch (err: any) {
            const message = err?.response?.data?.error || 'Failed to add rule';
            toast.error(message);
        } finally {
            setIsAddingRule(false);
        }
    };

    const handleDeleteRule = async (ruleId: string) => {
        if (!chain._id) return;
        try {
            await mutations.deleteRule(chain._id, ruleId);
            toast.success('Rule deleted');
            refetchRules();
        } catch {
            toast.error('Failed to delete rule');
        }
    };

    const handleRunRules = async () => {
        setIsRunningRules(true);
        try {
            const res = await mutations.runRules(true);
            toast.success(`Matched ${res?.matched ?? 0}, assigned ${res?.assigned ?? 0} shops`);
            refetchShops();
        } catch {
            toast.error('Run rules failed');
        } finally {
            setIsRunningRules(false);
        }
    };

    const toggleShopSelection = (id: string) => {
        setSelectedShopIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <div className="rounded-lg border border-ink/15 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start rounded-none border-b bg-muted/30">
                    <TabsTrigger value="details">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                        Details
                    </TabsTrigger>
                    <TabsTrigger value="shops">
                        <FontAwesomeIcon icon={faStore} className="mr-2" />
                        Shops ({total})
                    </TabsTrigger>
                    <TabsTrigger value="rules">
                        <FontAwesomeIcon icon={faMagic} className="mr-2" />
                        Rules
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="p-4 space-y-4">
                    <div>
                        <label className="text-sm font-medium">Name</label>
                        <p className="text-muted-foreground">{chain.name}</p>
                    </div>
                    {chain.description && (
                        <div>
                            <label className="text-sm font-medium">Description</label>
                            <p className="text-muted-foreground">{chain.description}</p>
                        </div>
                    )}
                    {chain.website && (
                        <div>
                            <label className="text-sm font-medium">Website</label>
                            <a href={chain.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                                {chain.website}
                            </a>
                        </div>
                    )}
                    {chain.country && (
                        <div>
                            <label className="text-sm font-medium">Country</label>
                            <p className="text-muted-foreground">{chain.country}</p>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="shops" className="p-4 space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Search and assign shops</label>
                        <div className="flex gap-2">
                            <Input
                                value={shopSearchQuery}
                                onChange={(e) => setShopSearchQuery(e.target.value)}
                                placeholder="Search by shop name..."
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchShops()}
                            />
                            <Button onClick={handleSearchShops} disabled={isSearching || !shopSearchQuery.trim()}>
                                {isSearching ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
                            </Button>
                        </div>
                    </div>

                    {shopSearchResults.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Select shops to assign</p>
                            <div className="max-h-40 overflow-y-auto space-y-1 border rounded p-2">
                                {shopSearchResults.map((s) => (
                                    <label key={s._id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 p-2 rounded">
                                        <input
                                            type="checkbox"
                                            checked={selectedShopIds.has(s._id)}
                                            onChange={() => toggleShopSelection(s._id)}
                                        />
                                        <span className="text-sm">{s.name}</span>
                                        {s.chainId && (
                                            <span className="text-xs text-muted-foreground">(in {s.chainId?.name || 'other chain'})</span>
                                        )}
                                    </label>
                                ))}
                            </div>
                            <Button onClick={handleAssignShops} disabled={selectedShopIds.size === 0} size="sm">
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                Assign {selectedShopIds.size} shop{selectedShopIds.size !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    )}

                    <div className="border-t pt-4">
                        <p className="text-sm font-medium mb-2">Shops in this chain</p>
                        {shopsLoading ? (
                            <div className="flex justify-center py-4">
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                            </div>
                        ) : shops.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No shops assigned</p>
                        ) : (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                                {shops.map((s) => (
                                    <div key={s._id} className="flex items-center justify-between gap-2 p-2 rounded hover:bg-muted/30">
                                        <span className="text-sm truncate">{s.name}</span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-destructive hover:text-destructive"
                                            onClick={() => handleUnassignShops([s._id])}
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="rules" className="p-4 space-y-4">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium">Auto-assignment rules (e.g. if name contains &quot;mikel&quot; → this chain)</p>
                        <Button onClick={handleRunRules} disabled={isRunningRules} size="sm">
                            {isRunningRules ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faPlay} />}
                            <span className="ml-2">Run rules now</span>
                        </Button>
                    </div>

                    <div className="flex gap-2 items-end">
                        <div className="flex-1 space-y-1">
                            <label className="text-xs">Value</label>
                            <Input
                                value={newRuleValue}
                                onChange={(e) => setNewRuleValue(e.target.value)}
                                placeholder="e.g. mikel"
                            />
                        </div>
                        <div className="w-32 space-y-1">
                            <label className="text-xs">Operator</label>
                            <select
                                value={newRuleOperator}
                                onChange={(e) => setNewRuleOperator(e.target.value as any)}
                                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                            >
                                <option value="contains">contains</option>
                                <option value="startsWith">starts with</option>
                                <option value="equals">equals</option>
                            </select>
                        </div>
                        <Button onClick={handleAddRule} disabled={isAddingRule || !newRuleValue.trim()}>
                            {isAddingRule ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faPlus} />}
                        </Button>
                    </div>

                    {rulesLoading ? (
                        <div className="flex justify-center py-4">
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        </div>
                    ) : rules.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No rules. Add one above.</p>
                    ) : (
                        <div className="space-y-2">
                            {rules.map((r) => (
                                <div key={r._id} className="flex items-center justify-between gap-2 p-2 rounded border">
                                    <span className="text-sm">
                                        name {r.operator} &quot;{r.value}&quot;
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => handleDeleteRule(r._id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
